import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Container,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  Home as HomeIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Assignment as ReportsIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon2,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

// MUI Components
import Checkbox from "../../components/MUI/Checkbox";
import RadioButton from "../../components/MUI/RadioButton";
import Slider from "../../components/MUI/Slider";
import Switch from "../../components/MUI/Switch";
import TextBox from "../../components/MUI/TextBox";
import SuggestionClip from "../../components/MUI/SuggestionClip";
import FilterClip, { FilterGroup } from "../../components/MUI/FilterClip";
import AssistiveClip from "../../components/MUI/AssistiveClip";
import InputClip from "../../components/MUI/InputClip";
import Tab, { Tabs } from "../../components/MUI/Tab";
import DatePicker from "../../components/MUI/DatePicker";
import Menu from "../../components/MUI/Menu";
import BasicDialog, { DialogPresets } from "../../components/MUI/BasicDialog";
import ListDialog from "../../components/MUI/ListDialog";
import ListScrollDialog from "../../components/MUI/ListScrollDialog";
import ListItem from "../../components/MUI/ListItem";
import Thumbnail from "../../components/MUI/Thumbnail";
import Monogram from "../../components/MUI/Monogram";
import List from "../../components/MUI/List";
import PlainTooltip from "../../components/MUI/PlainTooltip";
import LinearProgressIndicator from "../../components/MUI/LinearProgressIndicator";
import ProgressBarWidget from "../../components/MUI/ProgressBarWidget";
import NavigationDrawer from "../../components/MUI/NavigationDrawer";
import BottomNavigation from "../../components/MUI/BottomNavigation";
import TopAppBar from "../../components/MUI/TopAppBar";
import Snackbar, {
  successSnackbar,
  errorSnackbar,
  warningSnackbar,
  infoSnackbar,
} from "../../components/MUI/Snackbar";
import SmallFAB from "../../components/MUI/SmallFAB";
import ExpandedFAB from "../../components/MUI/ExpandedFAB";
import SearchBar from "../../components/MUI/SearcBar";
import SearchViewModal from "../../components/MUI/SearchViewModal";
import SegmentedButton from "../../components/MUI/SegmentedButton";
import TabSingleUnit from "../../components/MUI/TabSingleUnit";
import CustomDivider from "../../components/MUI/Divider";

import {
  dialogAlert,
  successAlert,
  errorAlert,
  warningAlert,
  infoAlert,
  dialogConfirm,
} from "../../components/MUI/DialogAlert";

import ButtonComp from "../../components/MUI/Button";
import { useDialog } from "../../Hooks/useDialog";
import { colorPrimary } from "../../Styles/Colors";
import FABs from "../../components/MUI/FABs";

import Cst from "./Cst";

const ComponentsViewer = () => {
  // State for interactive components
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [radioValue, setRadioValue] = useState("option1");
  const [sliderValue, setSliderValue] = useState(50);
  const [switchValue, setSwitchValue] = useState(false);
  const [textValue, setTextValue] = useState("");
  const [selectedFilters, setSelectedFilters] = useState(["Active"]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: null,
    end: null,
  });
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listScrollDialogOpen, setListScrollDialogOpen] = useState(false);
  const [selectedListItems, setSelectedListItems] = useState(new Set([1, 3]));
  const [listSwitchStates, setListSwitchStates] = useState({
    switch1: true,
    switch2: false,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const [currentPage, setCurrentPage] = useState("home");
  const [paperNavValue, setPaperNavValue] = useState("search");
  const [customNavValue, setCustomNavValue] = useState("dashboard");
  const [iconNavValue, setIconNavValue] = useState("home");

  // Snackbar states
  const [snackbars, setSnackbars] = useState({
    success: false,
    error: false,
    warning: false,
    info: false,
    simple: false,
  });

  // SearchBar states
  const [searchValue, setSearchValue] = useState("");
  const [searchFilters, setSearchFilters] = useState([
    { label: "All", active: true },
    { label: "Products", active: false },
    { label: "Users", active: false },
    { label: "Orders", active: false },
  ]);

  // SearchViewModal states
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [fullscreenSearchOpen, setFullscreenSearchOpen] = useState(false);
  const [compactSearchOpen, setCompactSearchOpen] = useState(false);

  // SegmentedButton states
  const [segmentedValue, setSegmentedValue] = useState("option1");
  const [multiSegmentedValue, setMultiSegmentedValue] = useState(["option1"]);
  const [verticalSegmentedValue, setVerticalSegmentedValue] = useState("tab1");

  // TabSingleUnit states
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabStates, setTabStates] = useState({
    tab1: true,
    tab2: false,
    tab3: false,
    tab4: true,
  });

  const { open, dialogProps, openDialog, closeDialog } = useDialog();

  const ComponentSection = ({ title, children }) => (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ color: "#1976d2", fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        {children}
      </Box>
    </Paper>
  );

  const [activeTab, setActiveTab] = useState(0);
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Tabs
        tabs={["MUI Components", "Custom Components"]}
        activeTab={activeTab}
        variant="outlined"
        onTabChange={(index) => setActiveTab(index)}
      />

      {activeTab == 0 ? (
        <Box mt="20px">
          {/* MUI Components Section */}
          <ComponentSection>
            <ButtonComp
              variant="contained"
              onClick={() => successAlert("Button clicked successfully!")}
            >
              Primary Button
            </ButtonComp>

            <ButtonComp
              variant="outlined"
              onClick={() => infoAlert("Outlined button was clicked!")}
            >
              Outlined Button
            </ButtonComp>

            <Checkbox
              checked={checkboxValue}
              onChange={(e) => setCheckboxValue(e.target.checked)}
              label="Checkbox Label"
            />

            <RadioButton
              checked={radioValue === "option1"}
              onChange={() => setRadioValue("option1")}
              value="option1"
              label="Radio Option 1"
            />

            <RadioButton
              checked={radioValue === "option2"}
              onChange={() => setRadioValue("option2")}
              value="option2"
              label="Radio Option 2"
            />

            <Switch
              checked={switchValue}
              onChange={(e) => setSwitchValue(e.target.checked)}
              label="Switch Control"
            />
          </ComponentSection>

          {/* Slider Section */}
          <ComponentSection title="Slider Component">
            <Box sx={{ width: "100%", maxWidth: 400 }}>
              <Slider
                value={sliderValue}
                onChange={(e, newValue) => setSliderValue(newValue)}
                label="Volume Control"
                min={0}
                max={100}
                showValue={true}
              />
            </Box>
          </ComponentSection>

          <ComponentSection title="Text Input Component">
            <TextBox
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              label="Enter text here"
              placeholder="Type something..."
            />
          </ComponentSection>

          {/* Suggestion Clips Section */}
          <ComponentSection title="Suggestion Clips">
            <SuggestionClip label="Default Chip" />
            <SuggestionClip label="Filled Chip" variant="filled" />
            <SuggestionClip label="Primary Chip" variant="primary" />
            <SuggestionClip
              label="Deletable Chip"
              variant="filled"
              onDelete={() =>
                dialogConfirm(
                  "Are you sure you want to delete this chip?",
                  () => successAlert("Chip deleted successfully!"),
                )
              }
            />
            <SuggestionClip
              label="Clickable Chip"
              onClick={() => infoAlert("Chip was clicked!")}
            />
          </ComponentSection>

          {/* Filter Clips Section */}
          <ComponentSection title="Filter Clips">
            <FilterClip
              label="Outlined Filter"
              variant="outlined"
              isSelected={false}
              onClick={() => infoAlert("Outlined filter clicked!")}
            />
            <FilterClip
              label="Selected Filter"
              variant="outlined"
              isSelected={true}
              onClick={() => infoAlert("Selected filter clicked!")}
            />
            <FilterClip
              label="Primary Filter"
              variant="primary"
              onClick={() => infoAlert("Primary filter clicked!")}
            />
            <FilterClip
              label="Secondary Filter"
              variant="secondary"
              isSelected={true}
              onClick={() => infoAlert("Secondary filter clicked!")}
            />
            <FilterClip
              label="Warning Filter"
              variant="warning"
              isSelected={false}
              onClick={() => infoAlert("Warning filter clicked!")}
            />
            <FilterClip
              label="Deletable Filter"
              variant="filled"
              isSelected={true}
              onDelete={() =>
                dialogConfirm(
                  "Are you sure you want to delete this filter?",
                  () => successAlert("Filter deleted successfully!"),
                )
              }
              onClick={() => infoAlert("Deletable filter clicked!")}
            />
            <FilterClip
              label="Trailing Icon Filter"
              variant="outlined"
              trailingIcon={<CloseIcon />}
              onTrailingIconClick={() => infoAlert("Trailing icon clicked!")}
              onClick={() => infoAlert("Filter with trailing clicked!")}
            />
          </ComponentSection>

          {/* Filter Group Section */}
          <ComponentSection title="Filter Group (Multiple Selection)">
            <Box sx={{ width: "100%" }}>
              <Typography variant="subtitle2" gutterBottom>
                Status Filters:
              </Typography>
              {/* <FilterGroup
                filters={["All", "Active", "Pending", "Completed", "Archived"]}
                selectedFilters={selectedFilters}
                onFilterChange={setSelectedFilters}
                variant="outlined"
                style={{ marginBottom: "16px" }}
              /> */}

              <Typography variant="subtitle2" gutterBottom>
                Category Filters (Secondary Style):
              </Typography>
              {/* <FilterGroup
                filters={[
                  { label: "Technology", value: "tech" },
                  { label: "Design", value: "design" },
                  { label: "Marketing", value: "marketing" },
                  { label: "Sales", value: "sales" },
                ]}
                selectedFilters={["tech", "design"]}
                onFilterChange={(selected) =>
                  console.log("Category filters:", selected)
                }
                variant="secondary"
              /> */}
            </Box>
          </ComponentSection>

          {/* Assistive Clips Section */}
          <ComponentSection title="Assistive Clips">
            <AssistiveClip
              label="Default Chip"
              variant="default"
              onClick={() => infoAlert("Default chip clicked!")}
            />
            <AssistiveClip
              label="Outlined Chip"
              variant="outlined"
              onClick={() => infoAlert("Outlined chip clicked!")}
            />
            <AssistiveClip
              label="Filled Chip"
              variant="filled"
              onClick={() => infoAlert("Filled chip clicked!")}
            />
            <AssistiveClip
              label="Success Status"
              status="success"
              onClick={() => successAlert("Success chip clicked!")}
            />
            <AssistiveClip
              label="Warning Status"
              status="warning"
              onClick={() => warningAlert("Warning chip clicked!")}
            />
            <AssistiveClip
              label="Error Status"
              status="error"
              onClick={() => errorAlert("Error chip clicked!")}
            />
            <AssistiveClip
              label="Info Status"
              status="info"
              onClick={() => infoAlert("Info chip clicked!")}
            />
            <AssistiveClip
              label="John Doe"
              variant="avatar"
              avatarText="JD"
              onClick={() => infoAlert("Avatar chip clicked!")}
            />
            <AssistiveClip
              label="User Profile"
              variant="avatar"
              onClick={() => infoAlert("Profile chip clicked!")}
            />
            <AssistiveClip
              label="Deletable Chip"
              status="success"
              onDelete={() =>
                dialogConfirm(
                  "Are you sure you want to delete this chip?",
                  () => successAlert("Chip deleted successfully!"),
                )
              }
              onClick={() => infoAlert("Deletable chip clicked!")}
            />
          </ComponentSection>

          {/* Input Clips Section */}
          <ComponentSection title="Input Chips">
            <InputClip
              label="Label only"
              variant="label-only"
              onClick={() => infoAlert("Label only clicked!")}
            />
            <InputClip
              label="Label"
              variant="label-trailing"
              onClick={() => infoAlert("Label with trailing clicked!")}
              onTrailingIconClick={() => infoAlert("Trailing icon clicked!")}
            />
            <InputClip
              label="Label"
              variant="leading-label"
              onClick={() => infoAlert("Leading label clicked!")}
            />
            <InputClip
              label="Label"
              variant="leading-trailing"
              onClick={() => infoAlert("Leading trailing clicked!")}
              onTrailingIconClick={() => infoAlert("Trailing icon clicked!")}
            />
            <InputClip
              label="Label"
              variant="label-avatar"
              avatarText="JD"
              onClick={() => infoAlert("Label avatar clicked!")}
            />
            <InputClip
              label="Label"
              variant="avatar-trailing"
              avatarText="AB"
              onClick={() => infoAlert("Avatar trailing clicked!")}
              onTrailingIconClick={() => infoAlert("Trailing icon clicked!")}
            />
            <InputClip
              label="Selected"
              variant="label-only"
              selected={true}
              onClick={() => infoAlert("Selected chip clicked!")}
            />
          </ComponentSection>

          {/* Tab Components Section */}
          <ComponentSection title="Tab Components">
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Default Tabs:
                </Typography>
                <Tabs
                  tabs={["Tab 1", "Tab 2", "Tab 3", "Tab 4"]}
                  activeTab={0}
                  variant="default"
                  onTabChange={(index) => console.log("Default tab:", index)}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Outlined Tabs:
                </Typography>
                <Tabs
                  tabs={["Tab 1", "Tab 2", "Tab 3", "Tab 4"]}
                  activeTab={1}
                  variant="outlined"
                  onTabChange={(index) => console.log("Outlined tab:", index)}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Underline Tabs:
                </Typography>
                <Tabs
                  tabs={["Tab 1", "Tab 2", "Tab 3", "Tab 4"]}
                  activeTab={2}
                  variant="underline"
                  onTabChange={(index) => console.log("Underline tab:", index)}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Individual Tabs:
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tab label="Active Tab" isActive={true} variant="default" />
                  <Tab
                    label="Inactive Tab"
                    isActive={false}
                    variant="default"
                  />
                  <Tab
                    label="Outlined Active"
                    isActive={true}
                    variant="outlined"
                  />
                  <Tab
                    label="Underline Active"
                    isActive={true}
                    variant="underline"
                  />
                </Box>
              </Box>
            </Box>
          </ComponentSection>

          {/* DatePicker Section */}
          <ComponentSection title="Date Picker">
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Single Date Picker:
                </Typography>
                <DatePicker
                  variant="single"
                  placeholder="Select a date"
                  value={selectedDate}
                  onChange={(date) => {
                    setSelectedDate(date);
                    console.log("Selected date:", date);
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Date Range Picker:
                </Typography>
                <DatePicker
                  variant="range"
                  placeholder="Select date range"
                  value={selectedDateRange}
                  onChange={(range) => {
                    setSelectedDateRange(range);
                    console.log("Selected range:", range);
                  }}
                />
              </Box>
            </Box>
          </ComponentSection>

          {/* Menu Section */}
          <ComponentSection title="Menu">
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Basic Menu:
                </Typography>
                <Menu
                  buttonText="Menu Item"
                  buttonVariant="contained"
                  variant="basic"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Outlined Menu:
                </Typography>
                <Menu
                  buttonText="Menu Item"
                  buttonVariant="outlined"
                  variant="basic"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Text Menu:
                </Typography>
                <Menu
                  buttonText="Menu Item"
                  buttonVariant="text"
                  variant="basic"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Icon Menu:
                </Typography>
                <Menu variant="icon" />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Custom Menu Items:
                </Typography>
                <Menu
                  buttonText="Custom Menu"
                  buttonVariant="contained"
                  menuItems={[
                    {
                      label: "Edit Profile",
                      icon: <CloseIcon />,
                      onClick: () => console.log("Edit Profile"),
                    },
                    {
                      label: "Settings",
                      icon: <CloseIcon />,
                      onClick: () => console.log("Settings"),
                    },
                    { type: "divider" },
                    {
                      label: "Logout",
                      icon: <CloseIcon />,
                      onClick: () => console.log("Logout"),
                    },
                  ]}
                />
              </Box>
            </Box>
          </ComponentSection>

          {/* BasicDialog Section */}
          <ComponentSection title="Basic Dialog">
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <ButtonComp
                variant="contained"
                onClick={() =>
                  dialogAlert(
                    "This is a basic dialog with custom content.",
                    "info",
                    "Basic Dialog",
                  )
                }
              >
                Basic Dialog
              </ButtonComp>

              <ButtonComp
                variant="contained"
                onClick={() =>
                  successAlert("Operation completed successfully!")
                }
              >
                Success Dialog
              </ButtonComp>

              <ButtonComp
                variant="contained"
                onClick={() =>
                  errorAlert("An error occurred while processing your request.")
                }
              >
                Error Dialog
              </ButtonComp>

              <ButtonComp
                variant="contained"
                onClick={() =>
                  warningAlert("This action will permanently delete the item.")
                }
              >
                Warning Dialog
              </ButtonComp>

              <ButtonComp
                variant="contained"
                onClick={() =>
                  infoAlert("Here is some helpful information for you.")
                }
              >
                Info Dialog
              </ButtonComp>

              <ButtonComp
                variant="contained"
                onClick={() =>
                  dialogConfirm(
                    "Are you sure you want to proceed with this action?",
                    () => {
                      successAlert("Action confirmed and completed!");
                    },
                  )
                }
              >
                Confirm Dialog
              </ButtonComp>
            </Box>
          </ComponentSection>

          {/* ListDialog Section */}
          <ComponentSection title="List Dialog">
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <ButtonComp
                variant="contained"
                onClick={() => setListDialogOpen(true)}
              >
                Open List Dialog
              </ButtonComp>

              <ButtonComp
                variant="outlined"
                onClick={() => setListDialogOpen(true)}
              >
                Show Selection Dialog
              </ButtonComp>
            </Box>
          </ComponentSection>

          {/* ListScrollDialog Section */}
          <ComponentSection title="Scrollable List Dialog">
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <ButtonComp
                variant="contained"
                onClick={() => setListScrollDialogOpen(true)}
              >
                Open Scrollable Dialog
              </ButtonComp>

              <ButtonComp
                variant="outlined"
                onClick={() => setListScrollDialogOpen(true)}
              >
                Show Long List
              </ButtonComp>
            </Box>
          </ComponentSection>

          {/* ListItem Section */}
          <ComponentSection title="List Items">
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* Basic List Items */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Basic List Items:
                </Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <ListItem
                    primaryText="Simple List Item"
                    onClick={() => infoAlert("Simple list item clicked!")}
                  />
                  <ListItem
                    primaryText="List Item with Secondary Text"
                    secondaryText="This is additional information"
                    divider
                    onClick={() =>
                      infoAlert("List item with secondary text clicked!")
                    }
                  />
                  <ListItem
                    primaryText="Disabled List Item"
                    secondaryText="This item is disabled"
                    disabled
                  />
                </Box>
              </Box>

              {/* Avatar List Items */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Avatar List Items:
                </Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <ListItem
                    variant="avatar"
                    avatarText="A"
                    primaryText="Alice Johnson"
                    secondaryText="alice.johnson@example.com"
                    status="online"
                    onClick={() => infoAlert("Alice clicked!")}
                  />
                  <ListItem
                    variant="avatar"
                    avatarText="B"
                    primaryText="Bob Smith"
                    secondaryText="bob.smith@example.com"
                    status="busy"
                    badge="Pro"
                    divider
                    onClick={() => infoAlert("Bob clicked!")}
                  />
                  <ListItem
                    variant="avatar"
                    avatarText="C"
                    primaryText="Carol Davis"
                    secondaryText="carol.davis@example.com"
                    status="away"
                    onClick={() => infoAlert("Carol clicked!")}
                  />
                </Box>
              </Box>

              {/* Interactive List Items */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Interactive List Items:
                </Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <ListItem
                    primaryText="Checkbox Item 1"
                    secondaryText="Click to select/deselect"
                    showCheckbox
                    checkboxChecked={selectedListItems.has(1)}
                    onCheckboxChange={(e) => {
                      const newSelected = new Set(selectedListItems);
                      if (e.target.checked) {
                        newSelected.add(1);
                      } else {
                        newSelected.delete(1);
                      }
                      setSelectedListItems(newSelected);
                    }}
                  />
                  <ListItem
                    primaryText="Checkbox Item 2"
                    secondaryText="Another selectable item"
                    showCheckbox
                    checkboxChecked={selectedListItems.has(2)}
                    onCheckboxChange={(e) => {
                      const newSelected = new Set(selectedListItems);
                      if (e.target.checked) {
                        newSelected.add(2);
                      } else {
                        newSelected.delete(2);
                      }
                      setSelectedListItems(newSelected);
                    }}
                    divider
                  />
                  <ListItem
                    primaryText="Switch Control 1"
                    secondaryText="Toggle this setting"
                    showSwitch
                    switchChecked={listSwitchStates.switch1}
                    onSwitchChange={(e) => {
                      setListSwitchStates((prev) => ({
                        ...prev,
                        switch1: e.target.checked,
                      }));
                    }}
                  />
                  <ListItem
                    primaryText="Switch Control 2"
                    secondaryText="Another toggle setting"
                    showSwitch
                    switchChecked={listSwitchStates.switch2}
                    onSwitchChange={(e) => {
                      setListSwitchStates((prev) => ({
                        ...prev,
                        switch2: e.target.checked,
                      }));
                    }}
                    divider
                  />
                  <ListItem
                    primaryText="More Options Item"
                    secondaryText="Click the menu icon for options"
                    showMoreOptions
                    onSecondaryAction={() => infoAlert("More options clicked!")}
                    onClick={() => infoAlert("List item clicked!")}
                  />
                </Box>
              </Box>

              {/* Different Sizes */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Different Sizes:
                </Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <ListItem
                    size="small"
                    avatarText="S"
                    primaryText="Small List Item"
                    secondaryText="Compact size"
                  />
                  <ListItem
                    size="medium"
                    avatarText="M"
                    primaryText="Medium List Item"
                    secondaryText="Default size"
                    divider
                  />
                  <ListItem
                    size="large"
                    avatarText="L"
                    primaryText="Large List Item"
                    secondaryText="Spacious size"
                  />
                </Box>
              </Box>
            </Box>
          </ComponentSection>

          {/* Thumbnail Section */}
          <ComponentSection title="Thumbnails">
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Thumbnail
                src="https://via.placeholder.com/120x120/1976d2/white?text=IMG1"
                alt="Sample 1"
              />
              <Thumbnail
                src="https://via.placeholder.com/150x100/9c27b0/white?text=IMG2"
                width={150}
                height={100}
                borderRadius={2}
                onClick={() => infoAlert("Thumbnail clicked!")}
              />
              <Thumbnail
                src="https://via.placeholder.com/80x80/f44336/white?text=IMG3"
                width={80}
                height={80}
                borderRadius="50%"
              />
            </Box>
          </ComponentSection>

          {/* Monogram Section */}
          <ComponentSection title="Monograms">
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Monogram name="John Doe" />
              <Monogram
                name="Alice Smith"
                size={60}
                backgroundColor="#9c27b0"
              />
              <Monogram
                name="Bob Wilson"
                size={50}
                variant="rounded"
                backgroundColor="#f44336"
              />
              <Monogram
                name="Carol Davis"
                size={40}
                variant="square"
                backgroundColor="#4caf50"
              />
              <Monogram
                name="David Miller"
                size={55}
                backgroundColor="#ff9800"
                onClick={() => infoAlert("Monogram clicked!")}
              />
            </Box>
          </ComponentSection>

          {/* List Section */}
          <ComponentSection title="Lists">
            <Box
              sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}
            >
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Default List:
                </Typography>
                <List>
                  <ListItem primaryText="Default List Item 1" />
                  <ListItem primaryText="Default List Item 2" />
                  <ListItem primaryText="Default List Item 3" />
                </List>
              </Box>

              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Paper List:
                </Typography>
                <List variant="paper" dense>
                  <ListItem
                    primaryText="Paper List Item 1"
                    secondaryText="Dense spacing"
                  />
                  <ListItem
                    primaryText="Paper List Item 2"
                    secondaryText="With elevation"
                  />
                </List>
              </Box>

              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Outlined List:
                </Typography>
                <List variant="outlined">
                  <ListItem avatarText="A" primaryText="Outlined Item 1" />
                  <ListItem avatarText="B" primaryText="Outlined Item 2" />
                </List>
              </Box>
            </Box>
          </ComponentSection>

          {/* Tooltip Section */}
          <ComponentSection title="Plain Tooltips">
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <PlainTooltip title="This is a basic tooltip">
                <ButtonComp variant="contained">Hover for tooltip</ButtonComp>
              </PlainTooltip>

              <PlainTooltip title="Tooltip with arrow" arrow placement="right">
                <ButtonComp variant="outlined">Right tooltip</ButtonComp>
              </PlainTooltip>

              <PlainTooltip title="Bottom placement tooltip" placement="bottom">
                <ButtonComp variant="text">Bottom tooltip</ButtonComp>
              </PlainTooltip>

              <PlainTooltip
                title="Custom styled tooltip with longer text content"
                sx={{ backgroundColor: colorPrimary, fontSize: "0.875rem" }}
              >
                <ButtonComp variant="contained" color="secondary">
                  Custom tooltip
                </ButtonComp>
              </PlainTooltip>
            </Box>
          </ComponentSection>

          {/* Linear Progress Section */}
          <ComponentSection title="Linear Progress Indicators">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Determinate Progress:
                </Typography>
                <LinearProgressIndicator value={75} showLabel />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Custom Label:
                </Typography>
                <LinearProgressIndicator
                  value={45}
                  label="Loading data... 45%"
                />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Different Colors:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <LinearProgressIndicator
                    value={30}
                    color="primary"
                    showLabel
                  />
                  <LinearProgressIndicator
                    value={60}
                    color="secondary"
                    showLabel
                  />
                  <LinearProgressIndicator
                    value={90}
                    color="success"
                    showLabel
                  />
                </Box>
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Indeterminate (Loading):
                </Typography>
                <LinearProgressIndicator variant="indeterminate" height={6} />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Custom Height:
                </Typography>
                <LinearProgressIndicator value={55} height={8} showLabel />
              </Box>
            </Box>
          </ComponentSection>

          {/* Progress Bar Widget Section */}
          <ComponentSection title="Progress Bar Widgets">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Default Widget:
                </Typography>
                <ProgressBarWidget title="Task Completion" value={75} />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Show Values:
                </Typography>
                <ProgressBarWidget
                  title="Files Uploaded"
                  value={45}
                  maxValue={60}
                  showValue
                />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Different Colors:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <ProgressBarWidget
                    title="Storage Used"
                    value={85}
                    color="warning"
                  />
                  <ProgressBarWidget
                    title="Download Progress"
                    value={92}
                    color="success"
                  />
                  <ProgressBarWidget
                    title="Memory Usage"
                    value={68}
                    color="error"
                  />
                </Box>
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Card Variant:
                </Typography>
                <ProgressBarWidget
                  variant="card"
                  title="Project Progress"
                  value={78}
                  color="primary"
                />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Simple Variant:
                </Typography>
                <ProgressBarWidget
                  variant="simple"
                  value={65}
                  color="secondary"
                  height={6}
                />
              </Box>
            </Box>
          </ComponentSection>

          {/* Navigation Drawer Section */}
          <ComponentSection title="Navigation Drawer">
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <ButtonComp
                variant="contained"
                onClick={() => setDrawerOpen(true)}
              >
                Open Drawer
              </ButtonComp>

              <ButtonComp
                variant="outlined"
                onClick={() => setDrawerOpen(true)}
              >
                Show Navigation
              </ButtonComp>
            </Box>
          </ComponentSection>

          {/* Top App Bar Section */}
          <ComponentSection title="Top App Bar">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example */}
              <Box sx={{ width: "100%" }}>
                <TopAppBar
                  title="Product"
                  showBackButton={true}
                  showMoreButton={true}
                  onBackClick={() => console.log("Back clicked")}
                  onMoreClick={() => console.log("More options clicked")}
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Other Variants:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1,
                          display: "block",
                          backgroundColor: "#f5f5f5",
                          fontWeight: 500,
                        }}
                      >
                        Title Only
                      </Typography>
                      <Box sx={{ height: "56px" }}>
                        <TopAppBar title="Title" showBackButton={true} />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1,
                          display: "block",
                          backgroundColor: "#f5f5f5",
                          fontWeight: 500,
                        }}
                      >
                        With Search
                      </Typography>
                      <Box sx={{ height: "56px" }}>
                        <TopAppBar title="Dashboard" showMenuButton={true} />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1,
                          display: "block",
                          backgroundColor: "#f5f5f5",
                          fontWeight: 500,
                        }}
                      >
                        With Actions
                      </Typography>
                      <Box sx={{ height: "56px" }}>
                        <TopAppBar
                          title="Details"
                          showBackButton={true}
                          showMoreButton={true}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1,
                          display: "block",
                          backgroundColor: "#f5f5f5",
                          fontWeight: 500,
                        }}
                      >
                        Center Aligned
                      </Typography>
                      <Box sx={{ height: "56px" }}>
                        <TopAppBar
                          title="Centered Title"
                          showMenuButton={false}
                          showMoreButton={false}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1,
                          display: "block",
                          backgroundColor: "#f5f5f5",
                          fontWeight: 500,
                        }}
                      >
                        Dense Variant
                      </Typography>
                      <Box sx={{ height: "48px" }}>
                        <TopAppBar
                          title="Compact"
                          sx={{
                            minHeight: "48px",
                            "& .MuiToolbar-root": { minHeight: "48px" },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1,
                          display: "block",
                          backgroundColor: "#f5f5f5",
                          fontWeight: 500,
                        }}
                      >
                        Default Style
                      </Typography>
                      <Box sx={{ height: "56px" }}>
                        <TopAppBar
                          title="My App"
                          showMenuButton={true}
                          sx={{
                            backgroundColor: "#1976d2",
                            color: "#ffffff",
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </ComponentSection>

          {/* Bottom Navigation Section */}
          <ComponentSection title="Bottom Navigation (Simplified Router API)">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 500 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Main App Navigation:
                </Typography>
                <BottomNavigation
                  actions={[
                    { label: "Home", icon: <HomeIcon />, path: "/" },
                    {
                      label: "Favorites",
                      icon: <FavoriteIcon />,
                      path: "/favorites",
                    },
                    {
                      label: "Nearby",
                      icon: <LocationIcon />,
                      path: "/nearby",
                    },
                    {
                      label: "Recents",
                      icon: <RestoreIcon />,
                      path: "/recents",
                    },
                  ]}
                  onNavigate={(path, action) => {
                    console.log(`Navigated to: ${path}`, action);
                  }}
                />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 500 }}>
                <Typography variant="subtitle2" gutterBottom>
                  User Section (Paper Variant):
                </Typography>
                <BottomNavigation
                  variant="paper"
                  actions={[
                    {
                      label: "Search",
                      icon: <SearchIcon />,
                      path: "/search",
                      sx: { "&.Mui-selected": { color: "#ff9800" } },
                    },
                    {
                      label: "Profile",
                      icon: <PersonIcon />,
                      path: "/profile",
                      sx: { "&.Mui-selected": { color: "#4caf50" } },
                    },
                    {
                      label: "Settings",
                      icon: <SettingsIcon />,
                      path: "/settings",
                      sx: { "&.Mui-selected": { color: "#f44336" } },
                    },
                  ]}
                  sx={{
                    "& .MuiBottomNavigationAction-root": {
                      "&.Mui-selected": {
                        fontSize: "0.875rem",
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 500 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Admin Dashboard:
                </Typography>
                <BottomNavigation
                  actions={[
                    {
                      label: "Dashboard",
                      icon: <DashboardIcon />,
                      path: "/admin/dashboard",
                      onClick: (path, action) =>
                        console.log("Dashboard clicked:", path),
                    },
                    {
                      label: "Analytics",
                      icon: <AnalyticsIcon />,
                      path: "/admin/analytics",
                      sx: { "&.Mui-selected": { color: "#9c27b0" } },
                    },
                    {
                      label: "Reports",
                      icon: <ReportsIcon />,
                      path: "/admin/reports",
                    },
                    {
                      label: "Notifications",
                      icon: <NotificationsIcon />,
                      path: "/admin/notifications",
                      sx: { "&.Mui-selected": { color: "#ff5722" } },
                    },
                  ]}
                  sx={{
                    bgcolor: "#f3f4f6",
                    borderRadius: "12px",
                    "& .MuiBottomNavigationAction-root": {
                      borderRadius: "8px",
                      margin: "4px",
                      "&.Mui-selected": {
                        bgcolor: "rgba(25, 118, 210, 0.1)",
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ width: "100%", maxWidth: 500 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Icon Only Navigation:
                </Typography>
                <BottomNavigation
                  showLabels={false}
                  actions={[
                    {
                      label: "Home",
                      icon: <HomeIcon />,
                      path: "/quick/home",
                      sx: { "&.Mui-selected": { color: "#2196f3" } },
                    },
                    {
                      label: "Search",
                      icon: <SearchIcon />,
                      path: "/quick/search",
                      sx: { "&.Mui-selected": { color: "#ff9800" } },
                    },
                    {
                      label: "Favorites",
                      icon: <FavoriteIcon />,
                      path: "/quick/favorites",
                      sx: { "&.Mui-selected": { color: "#e91e63" } },
                    },
                  ]}
                  sx={{
                    maxWidth: "300px",
                    borderRadius: "25px",
                    bgcolor: "#fff",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            </Box>
          </ComponentSection>

          {/* Small FAB Section */}
          <ComponentSection title="Small Floating Action Button (FAB)">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example - Figma Style */}
              <Box sx={{ width: "100%" }}>
                {/* Surface Variant */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Surface
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <SmallFAB
                      sx={{
                        borderRadius: "12px",
                        background: "#F3E4EA",
                        boxShadow:
                          "0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.30)",
                      }}
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 1 clicked!")}
                    />
                    <SmallFAB
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 2 clicked!")}
                    />
                    <SmallFAB
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 3 clicked!")}
                    />
                    <SmallFAB
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 4 clicked!")}
                    />
                  </Box>
                </Box>

                {/* Primary Variant */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Primary
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <SmallFAB
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 1 clicked!")}
                    />
                    <SmallFAB
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 2 clicked!")}
                    />
                    <SmallFAB
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 3 clicked!")}
                    />
                    <SmallFAB
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 4 clicked!")}
                    />
                  </Box>
                </Box>

                {/* Secondary Variant */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Secondary
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <SmallFAB
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 1 clicked!")}
                    />
                    <SmallFAB
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 2 clicked!")}
                    />
                    <SmallFAB
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 3 clicked!")}
                    />
                    <SmallFAB
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 4 clicked!")}
                    />
                  </Box>
                </Box>

                {/* Tertiary Variant */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Tertiary
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <SmallFAB
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 1 clicked!")}
                    />
                    <SmallFAB
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 2 clicked!")}
                    />
                    <SmallFAB
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 3 clicked!")}
                    />
                    <SmallFAB
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 4 clicked!")}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </ComponentSection>
          <ComponentSection title="Floating Action Button (FAB)">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example - Figma Style */}
              <Box sx={{ width: "100%" }}>
                {/* Surface Variant */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Surface
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FABs
                      sx={{
                        borderRadius: "12px",
                        background: "#F3E4EA",
                        boxShadow:
                          "0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.30)",
                      }}
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 1 clicked!")}
                    />
                    <FABs
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 2 clicked!")}
                    />
                    <FABs
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 3 clicked!")}
                    />
                    <FABs
                      variant="surface"
                      onClick={() => infoAlert("Surface FAB 4 clicked!")}
                    />
                  </Box>
                </Box>

                {/* Primary Variant */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Primary
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FABs
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 1 clicked!")}
                    />
                    <FABs
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 2 clicked!")}
                    />
                    <FABs
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 3 clicked!")}
                    />
                    <FABs
                      variant="primary"
                      onClick={() => infoAlert("Primary FAB 4 clicked!")}
                    />
                  </Box>
                </Box>

                {/* Secondary Variant */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Secondary
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FABs
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 1 clicked!")}
                    />
                    <FABs
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 2 clicked!")}
                    />
                    <FABs
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 3 clicked!")}
                    />
                    <FABs
                      variant="secondary"
                      onClick={() => infoAlert("Secondary FAB 4 clicked!")}
                    />
                  </Box>
                </Box>

                {/* Tertiary Variant */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Tertiary
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FABs
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 1 clicked!")}
                    />
                    <FABs
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 2 clicked!")}
                    />
                    <FABs
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 3 clicked!")}
                    />
                    <FABs
                      variant="tertiary"
                      onClick={() => infoAlert("Tertiary FAB 4 clicked!")}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </ComponentSection>

          {/* Extended FAB Section */}
          <ComponentSection title="Extended Floating Action Button (FAB)">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example - Figma Style */}
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{
                    p: 3,
                    border: "2px solid #1976d2",
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.1)",
                  }}
                >
                  {/* Surface Variant */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Surface
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <ExpandedFAB
                        variant="surface"
                        label="Label"
                        onClick={() =>
                          infoAlert("Surface Extended FAB 1 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="surface"
                        label="Label"
                        onClick={() =>
                          infoAlert("Surface Extended FAB 2 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="surface"
                        label="Label"
                        onClick={() =>
                          infoAlert("Surface Extended FAB 3 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="surface"
                        label="Label"
                        onClick={() =>
                          infoAlert("Surface Extended FAB 4 clicked!")
                        }
                      />
                    </Box>
                  </Box>

                  {/* Primary Variant */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Primary
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <ExpandedFAB
                        variant="primary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Primary Extended FAB 1 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="primary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Primary Extended FAB 2 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="primary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Primary Extended FAB 3 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="primary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Primary Extended FAB 4 clicked!")
                        }
                      />
                    </Box>
                  </Box>

                  {/* Secondary Variant */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Secondary
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <ExpandedFAB
                        variant="secondary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Secondary Extended FAB 1 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="secondary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Secondary Extended FAB 2 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="secondary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Secondary Extended FAB 3 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="secondary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Secondary Extended FAB 4 clicked!")
                        }
                      />
                    </Box>
                  </Box>

                  {/* Tertiary Variant */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Tertiary
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <ExpandedFAB
                        variant="tertiary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Tertiary Extended FAB 1 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="tertiary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Tertiary Extended FAB 2 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="tertiary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Tertiary Extended FAB 3 clicked!")
                        }
                      />
                      <ExpandedFAB
                        variant="tertiary"
                        label="Label"
                        onClick={() =>
                          infoAlert("Tertiary Extended FAB 4 clicked!")
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Additional Examples */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Usage Examples:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, display: "block", mb: 2 }}
                      >
                        Custom Labels
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "center",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <ExpandedFAB
                          variant="primary"
                          label="Edit Item"
                          tooltip="Edit the selected item"
                          onClick={() => infoAlert("Edit action!")}
                        />
                        <ExpandedFAB
                          variant="tertiary"
                          label="Save Changes"
                          tooltip="Save all changes"
                          onClick={() => successAlert("Save action!")}
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, display: "block", mb: 2 }}
                      >
                        Different States
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "center",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <ExpandedFAB
                          variant="surface"
                          label="Active"
                          tooltip="Normal active state"
                          onClick={() => infoAlert("Active FAB!")}
                        />
                        <ExpandedFAB
                          variant="primary"
                          label="Disabled"
                          disabled
                          tooltip="Disabled state"
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </ComponentSection>

          <ComponentSection title="SearchBar">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example */}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Default Search Bar */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Default Search Bar with Menu
                  </Typography>
                  <SearchBar
                    variant="default"
                    showMenu={true}
                    placeholder="Hinted search text"
                    onSearch={(value) =>
                      successAlert(`Searching for: ${value}`)
                    }
                  />
                </Box>

                {/* Search Bar with Avatar */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Search Bar with Avatar
                  </Typography>
                  <SearchBar
                    variant="withAvatar"
                    showMenu={true}
                    showAvatar={true}
                    placeholder="Hinted search text"
                    avatarLetter="A"
                    onSearch={(value) =>
                      successAlert(`Searching for: ${value}`)
                    }
                  />
                </Box>

                {/* Search Modal */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Search View Modal
                  </Typography>
                  <SearchBar
                    variant="modal"
                    showBack={true}
                    showMenu={false}
                    placeholder="Input text"
                    onSearch={(value) => successAlert(`Modal search: ${value}`)}
                  />
                </Box>
              </Box>
            </Box>
          </ComponentSection>

          <ComponentSection title="SearchViewModal">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  Featured: Search Modal with Results
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <ButtonComp
                    variant="contained"
                    onClick={() => setSearchModalOpen(true)}
                  >
                    Open Search Modal
                  </ButtonComp>
                  <ButtonComp
                    variant="outlined"
                    onClick={() => setFullscreenSearchOpen(true)}
                  >
                    Fullscreen Search
                  </ButtonComp>
                  <ButtonComp
                    variant="text"
                    onClick={() => setCompactSearchOpen(true)}
                  >
                    Compact Search
                  </ButtonComp>
                </Box>
              </Box>

              {/* Examples Grid */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Different Modal Variants:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, display: "block", mb: 2 }}
                      >
                        Standard Modal
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        Regular sized search modal with back button and close
                        button
                      </Typography>
                      <ButtonComp
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => setSearchModalOpen(true)}
                      >
                        Open Modal
                      </ButtonComp>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, display: "block", mb: 2 }}
                      >
                        Fullscreen Modal
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        Fullscreen search experience for mobile-first design
                      </Typography>
                      <ButtonComp
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => setFullscreenSearchOpen(true)}
                      >
                        Open Fullscreen
                      </ButtonComp>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, display: "block", mb: 2 }}
                      >
                        Compact Modal
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        Minimal search modal without back button
                      </Typography>
                      <ButtonComp
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => setCompactSearchOpen(true)}
                      >
                        Open Compact
                      </ButtonComp>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </ComponentSection>

          <ComponentSection title="SegmentedButton">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  Featured: Standard Segmented Button
                </Typography>
                <SegmentedButton
                  options={[
                    { value: "label1", label: "Label" },
                    { value: "label2", label: "Label" },
                  ]}
                  value={segmentedValue}
                  onChange={(event, newValue) => setSegmentedValue(newValue)}
                />
              </Box>

              {/* Variants Grid */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#1976d2", mb: 2 }}
                >
                  Variants
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Outlined Variant
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Segmented button with outlined style
                      </Typography>
                      <SegmentedButton
                        variant="outlined"
                        options={[
                          { value: "label1", label: "Label" },
                          { value: "label2", label: "Label" },
                          { value: "label3", label: "Label" },
                        ]}
                        value={segmentedValue}
                        onChange={(event, newValue) =>
                          setSegmentedValue(newValue)
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Filled Variant
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Segmented button with filled background
                      </Typography>
                      <SegmentedButton
                        variant="filled"
                        options={[
                          { value: "label1", label: "Label" },
                          { value: "label2", label: "Label" },
                          { value: "label3", label: "Label" },
                          { value: "label4", label: "Label" },
                        ]}
                        value={segmentedValue}
                        onChange={(event, newValue) =>
                          setSegmentedValue(newValue)
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Multi-Select
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Allow multiple selections
                      </Typography>
                      <SegmentedButton
                        exclusive={false}
                        options={[
                          { value: "label1", label: "Label" },
                          { value: "label2", label: "Label" },
                          { value: "label3", label: "Label" },
                          { value: "label4", label: "Label" },
                          { value: "label5", label: "Label" },
                        ]}
                        value={multiSegmentedValue}
                        onChange={(event, newValue) =>
                          setMultiSegmentedValue(newValue)
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Vertical Orientation
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Vertical segmented button layout
                      </Typography>
                      <SegmentedButton
                        orientation="vertical"
                        options={[
                          { value: "tab1", label: "Dashboard" },
                          { value: "tab2", label: "Analytics" },
                          { value: "tab3", label: "Reports" },
                          { value: "tab4", label: "Settings" },
                        ]}
                        value={verticalSegmentedValue}
                        onChange={(event, newValue) =>
                          setVerticalSegmentedValue(newValue)
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        With Icons
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Segmented buttons with icons
                      </Typography>
                      <SegmentedButton
                        options={[
                          { value: "home", label: "Home", icon: <HomeIcon /> },
                          {
                            value: "search",
                            label: "Search",
                            icon: <SearchIcon />,
                          },
                          {
                            value: "person",
                            label: "Profile",
                            icon: <PersonIcon />,
                          },
                        ]}
                        value={segmentedValue}
                        onChange={(event, newValue) =>
                          setSegmentedValue(newValue)
                        }
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </ComponentSection>

          <ComponentSection title="TabSingleUnit">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example */}
              <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Primary variants */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: "#8E4EC6" }}>
                      Primary Variants
                    </Typography>

                    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {/* Primary - Icon and Label */}
                      <Box sx={{ borderBottom: "1px solid #E0E0E0", pb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 1, color: "#666" }}
                        >
                          Icon and Label
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TabSingleUnit
                            variant="primary"
                            type="icon-and-label"
                            label="Tab"
                            icon={<HomeIcon />}
                            value={0}
                            selected={selectedTab === 0}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            variant="primary"
                            type="icon-and-label"
                            label="Tab"
                            icon={<PersonIcon />}
                            value={1}
                            selected={selectedTab === 1}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                      </Box>

                      {/* Primary - Icon Only */}
                      <Box sx={{ borderBottom: "1px solid #E0E0E0", pb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 1, color: "#666" }}
                        >
                          Icon Only
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TabSingleUnit
                            variant="primary"
                            type="icon-only"
                            icon={<SettingsIcon />}
                            value={2}
                            selected={selectedTab === 2}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            variant="primary"
                            type="icon-only"
                            icon={<NotificationsIcon />}
                            value={3}
                            selected={selectedTab === 3}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                      </Box>

                      {/* Primary - Label Only */}
                      <Box sx={{ borderBottom: "1px solid #E0E0E0", pb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 1, color: "#666" }}
                        >
                          Label Only
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TabSingleUnit
                            variant="primary"
                            type="label-only"
                            label="Tab"
                            value={4}
                            selected={selectedTab === 4}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            variant="primary"
                            type="label-only"
                            label="Tab"
                            value={5}
                            selected={selectedTab === 5}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Secondary variants */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: "#1976D2" }}>
                      Secondary Variants
                    </Typography>

                    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {/* Secondary - Label Only */}
                      <Box sx={{ borderBottom: "1px solid #E0E0E0", pb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 1, color: "#666" }}
                        >
                          Label Only
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TabSingleUnit
                            variant="secondary"
                            type="label-only"
                            label="Tab"
                            value={6}
                            selected={selectedTab === 6}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            variant="secondary"
                            type="label-only"
                            label="Tab"
                            value={7}
                            selected={selectedTab === 7}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                      </Box>

                      {/* Secondary - Icon and Label */}
                      <Box sx={{ borderBottom: "1px solid #E0E0E0", pb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 1, color: "#666" }}
                        >
                          Icon and Label
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TabSingleUnit
                            variant="secondary"
                            type="icon-and-label"
                            label="Tab"
                            icon={<DashboardIcon />}
                            value={8}
                            selected={selectedTab === 8}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            variant="secondary"
                            type="icon-and-label"
                            label="Tab"
                            icon={<AnalyticsIcon />}
                            value={9}
                            selected={selectedTab === 9}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Variants Grid */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#1976d2", mb: 2 }}
                >
                  Variants
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Pill Variant
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Pill-shaped tabs for modern interfaces
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <TabSingleUnit
                          variant="pill"
                          label="Home"
                          value={0}
                          selected={tabStates.tab1}
                          icon={<HomeIcon />}
                          onClick={() =>
                            setTabStates((prev) => ({
                              ...prev,
                              tab1: !prev.tab1,
                            }))
                          }
                        />
                        <TabSingleUnit
                          variant="pill"
                          label="Profile"
                          value={1}
                          selected={tabStates.tab2}
                          icon={<PersonIcon />}
                          onClick={() =>
                            setTabStates((prev) => ({
                              ...prev,
                              tab2: !prev.tab2,
                            }))
                          }
                        />
                        <TabSingleUnit
                          variant="pill"
                          label="Notifications"
                          value={2}
                          selected={tabStates.tab3}
                          badge="5"
                          icon={<NotificationsIcon />}
                          onClick={() =>
                            setTabStates((prev) => ({
                              ...prev,
                              tab3: !prev.tab3,
                            }))
                          }
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Closable Tabs
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Tabs with close functionality
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          borderBottom: "1px solid #E0E0E0",
                        }}
                      >
                        <TabSingleUnit
                          label="Document.pdf"
                          value={0}
                          selected={selectedTab === 0}
                          closable
                          onClick={(event, value) => setSelectedTab(value)}
                          onClose={() => successAlert("Tab closed!")}
                        />
                        <TabSingleUnit
                          label="Image.jpg"
                          value={1}
                          selected={selectedTab === 1}
                          closable
                          onClick={(event, value) => setSelectedTab(value)}
                          onClose={() => successAlert("Tab closed!")}
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Vertical Tabs
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Vertical orientation for sidebars
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "fit-content",
                        }}
                      >
                        <TabSingleUnit
                          variant="vertical"
                          label="Dashboard"
                          value={0}
                          selected={tabStates.tab1}
                          icon={<DashboardIcon />}
                          showDivider
                          onClick={() =>
                            setTabStates((prev) => ({
                              ...prev,
                              tab1: true,
                              tab2: false,
                              tab3: false,
                            }))
                          }
                        />
                        <TabSingleUnit
                          variant="vertical"
                          label="Analytics"
                          value={1}
                          selected={tabStates.tab2}
                          icon={<AnalyticsIcon />}
                          badge="2"
                          showDivider
                          onClick={() =>
                            setTabStates((prev) => ({
                              ...prev,
                              tab1: false,
                              tab2: true,
                              tab3: false,
                            }))
                          }
                        />
                        <TabSingleUnit
                          variant="vertical"
                          label="Settings"
                          value={2}
                          selected={tabStates.tab3}
                          icon={<SettingsIcon />}
                          onClick={() =>
                            setTabStates((prev) => ({
                              ...prev,
                              tab1: false,
                              tab2: false,
                              tab3: true,
                            }))
                          }
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Different Sizes
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Small, medium, and large tab sizes
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            borderBottom: "1px solid #E0E0E0",
                          }}
                        >
                          <TabSingleUnit
                            size="small"
                            label="Small Tab"
                            value={0}
                            selected={selectedTab === 0}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            size="small"
                            label="Small Tab"
                            value={1}
                            selected={selectedTab === 1}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            borderBottom: "1px solid #E0E0E0",
                          }}
                        >
                          <TabSingleUnit
                            size="large"
                            label="Large Tab"
                            value={0}
                            selected={selectedTab === 0}
                            icon={<HomeIcon />}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                          <TabSingleUnit
                            size="large"
                            label="Large Tab"
                            value={1}
                            selected={selectedTab === 1}
                            icon={<PersonIcon />}
                            onClick={(event, value) => setSelectedTab(value)}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </ComponentSection>

          <ComponentSection title="Divider">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* All Divider Variants - One Example Each */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  Divider Variants
                </Typography>

                <Grid container spacing={3}>
                  {/* Inset Vertical */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Inset Vertical
                    </Typography>
                    <Box
                      sx={{
                        height: "100px",
                        display: "flex",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        padding: "8px",
                      }}
                    >
                      <CustomDivider variant="inset-vertical" length="100px" />
                    </Box>
                  </Grid>

                  {/* Middle Inset Vertical */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Middle Inset Vertical
                    </Typography>
                    <Box
                      sx={{
                        height: "100px",
                        display: "flex",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        padding: "8px",
                      }}
                    >
                      <CustomDivider
                        variant="middle-inset-vertical"
                        length="100px"
                      />
                    </Box>
                  </Grid>

                  {/* Full-Width Horizontal */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Full-Width Horizontal
                    </Typography>
                    <Box sx={{ backgroundColor: "#f5f5f5", padding: "8px" }}>
                      <CustomDivider variant="full-width-horizontal" />
                    </Box>
                  </Grid>

                  {/* Inset Horizontal */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Inset Horizontal
                    </Typography>
                    <Box sx={{ backgroundColor: "#f5f5f5", padding: "8px" }}>
                      <CustomDivider variant="inset-horizontal" />
                    </Box>
                  </Grid>

                  {/* Middle Inset Horizontal */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Middle Inset Horizontal
                    </Typography>
                    <Box sx={{ backgroundColor: "#f5f5f5", padding: "8px" }}>
                      <CustomDivider variant="middle-inset-horizontal" />
                    </Box>
                  </Grid>

                  {/* Text Insert Example */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Text Insert - OR
                    </Typography>
                    <Box sx={{ backgroundColor: "#f5f5f5", padding: "8px" }}>
                      <CustomDivider
                        variant="full-width-horizontal"
                        textAlign="center"
                      >
                        OR
                      </CustomDivider>
                    </Box>
                  </Grid>

                  {/* Inset Text Insert Example */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Text Insert - AND
                    </Typography>
                    <Box sx={{ backgroundColor: "#f5f5f5", padding: "8px" }}>
                      <CustomDivider
                        variant="inset-horizontal"
                        textAlign="center"
                      >
                        AND
                      </CustomDivider>
                    </Box>
                  </Grid>

                  {/* Middle Inset Text Insert Example */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Text Insert - END
                    </Typography>
                    <Box sx={{ backgroundColor: "#f5f5f5", padding: "8px" }}>
                      <CustomDivider
                        variant="middle-inset-horizontal"
                        textAlign="center"
                      >
                        END
                      </CustomDivider>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </ComponentSection>

          <ComponentSection title="Snackbar">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Main Featured Example */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  Featured: Different Severity Types
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <ButtonComp
                    variant="contained"
                    color="success"
                    now
                    look
                    onClick={() =>
                      setSnackbars((prev) => ({ ...prev, success: true }))
                    }
                  >
                    Success Snackbar
                  </ButtonComp>

                  <ButtonComp
                    variant="contained"
                    color="error"
                    onClick={() =>
                      setSnackbars((prev) => ({ ...prev, error: true }))
                    }
                  >
                    Error Snackbar
                  </ButtonComp>

                  <ButtonComp
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      setSnackbars((prev) => ({ ...prev, warning: true }))
                    }
                  >
                    Warning Snackbar
                  </ButtonComp>

                  <ButtonComp
                    variant="contained"
                    color="info"
                    onClick={() =>
                      setSnackbars((prev) => ({ ...prev, info: true }))
                    }
                  >
                    Info Snackbar
                  </ButtonComp>

                  <ButtonComp
                    variant="outlined"
                    onClick={() =>
                      setSnackbars((prev) => ({ ...prev, simple: true }))
                    }
                  >
                    Simple Snackbar
                  </ButtonComp>
                </Box>
              </Box>
            </Box>
          </ComponentSection>
        </Box>
      ) : <Cst />}

      <BasicDialog open={open} onClose={closeDialog} {...dialogProps} />

      <ListDialog
        open={listDialogOpen}
        onClose={() => setListDialogOpen(false)}
        title="Select Items"
        description="Choose the items you want to include in your selection. You can select multiple items by clicking on them."
        items={[
          { id: 1, text: "Project Alpha", percentage: "85%", avatar: "A" },
          { id: 2, text: "Project Beta", percentage: "92%", avatar: "B" },
          { id: 3, text: "Project Gamma", percentage: "76%", avatar: "G" },
          { id: 4, text: "Project Delta", percentage: "100%", avatar: "D" },
        ]}
        primaryAction="Apply Selection"
        secondaryAction="Cancel"
        onPrimaryAction={(selectedIds) => {
          console.log("Selected items:", selectedIds);
          successAlert(`Selected ${selectedIds.length} item(s)`);
          setListDialogOpen(false);
        }}
        onSecondaryAction={() => {
          console.log("Selection cancelled");
          setListDialogOpen(false);
        }}
      />

      <ListScrollDialog
        open={listScrollDialogOpen}
        onClose={() => setListScrollDialogOpen(false)}
        title="Scrollable Item Selection"
        description="This dialog contains a long list of items that you can scroll through. Select multiple items and see the scrolling behavior in action."
        items={[
          {
            id: 1,
            text: "Project Alpha - Web Development",
            percentage: "85%",
            avatar: "A",
          },
          {
            id: 2,
            text: "Project Beta - Mobile App",
            percentage: "92%",
            avatar: "B",
          },
          {
            id: 3,
            text: "Project Gamma - Data Analysis",
            percentage: "76%",
            avatar: "G",
          },
          {
            id: 4,
            text: "Project Delta - Machine Learning",
            percentage: "100%",
            avatar: "D",
          },
          {
            id: 5,
            text: "Project Epsilon - Cloud Migration",
            percentage: "68%",
            avatar: "E",
          },
          {
            id: 6,
            text: "Project Zeta - Security Audit",
            percentage: "89%",
            avatar: "Z",
          },
          {
            id: 7,
            text: "Project Theta - UI/UX Design",
            percentage: "94%",
            avatar: "T",
          },
          {
            id: 8,
            text: "Project Lambda - DevOps Setup",
            percentage: "77%",
            avatar: "L",
          },
          {
            id: 9,
            text: "Project Kappa - Testing Framework",
            percentage: "91%",
            avatar: "K",
          },
          {
            id: 10,
            text: "Project Sigma - Performance Optimization",
            percentage: "83%",
            avatar: "S",
          },
          {
            id: 11,
            text: "Project Omega - Integration Services",
            percentage: "96%",
            avatar: "O",
          },
          {
            id: 12,
            text: "Project Phi - Documentation Portal",
            percentage: "72%",
            avatar: "P",
          },
          {
            id: 13,
            text: "Project Chi - Monitoring System",
            percentage: "88%",
            avatar: "C",
          },
          {
            id: 14,
            text: "Project Psi - Backup Solution",
            percentage: "95%",
            avatar: "Y",
          },
          {
            id: 15,
            text: "Project Mu - Content Management",
            percentage: "81%",
            avatar: "M",
          },
        ]}
        primaryAction="Apply Selection"
        secondaryAction="Cancel"
        onPrimaryAction={(selectedIds) => {
          console.log("Scroll dialog selected items:", selectedIds);
          successAlert(
            `Selected ${selectedIds.length} item(s) from scrollable list`,
          );
          setListScrollDialogOpen(false);
        }}
        onSecondaryAction={() => {
          console.log("Scroll dialog selection cancelled");
          setListScrollDialogOpen(false);
        }}
      />

      <NavigationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant="temporary"
        title="Navigation Drawer"
        sections={[
          {
            header: "Main Navigation",
            items: [
              {
                label: "Dashboard",
                value: "5",
                onClick: () => infoAlert("Dashboard clicked!"),
              },
              {
                label: "Analytics",
                value: "12+",
                onClick: () => infoAlert("Analytics clicked!"),
              },
              {
                label: "Reports",
                value: "8",
                onClick: () => infoAlert("Reports clicked!"),
              },
              {
                label: "Users",
                value: "100+",
                onClick: () => infoAlert("Users clicked!"),
              },
            ],
          },
          {
            header: "Settings",
            items: [
              {
                label: "Profile",
                value: "",
                onClick: () => infoAlert("Profile clicked!"),
              },
              {
                label: "Preferences",
                value: "",
                onClick: () => infoAlert("Preferences clicked!"),
              },
              {
                label: "Security",
                value: "",
                onClick: () => infoAlert("Security clicked!"),
              },
            ],
          },
          {
            header: "Support",
            items: [
              {
                label: "Help Center",
                value: "",
                onClick: () => infoAlert("Help clicked!"),
              },
              {
                label: "Contact Us",
                value: "",
                onClick: () => infoAlert("Contact clicked!"),
              },
              {
                label: "Feedback",
                value: "",
                onClick: () => infoAlert("Feedback clicked!"),
              },
            ],
          },
        ]}
      />

      {/* Snackbar Components */}
      <Snackbar
        open={snackbars.success}
        message="Operation completed successfully!"
        severity="success"
        onClose={() => setSnackbars((prev) => ({ ...prev, success: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />

      <Snackbar
        open={snackbars.error}
        message="An error occurred while processing your request."
        severity="error"
        onClose={() => setSnackbars((prev) => ({ ...prev, error: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Snackbar
        open={snackbars.warning}
        message="Please check your input and try again."
        severity="warning"
        onClose={() => setSnackbars((prev) => ({ ...prev, warning: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />

      <Snackbar
        open={snackbars.info}
        message="Here's some helpful information for you."
        severity="info"
        onClose={() => setSnackbars((prev) => ({ ...prev, info: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />

      <Snackbar
        open={snackbars.simple}
        message="This is a simple snackbar without severity."
        severity="none"
        onClose={() => setSnackbars((prev) => ({ ...prev, simple: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />

      {/* Search View Modal Instances */}
      <SearchViewModal
        variant="standard"
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        searchResults={[
          {
            id: 1,
            title: "List item",
            subtitle: "Supporting line text lorem ipsum dolor...",
            avatar: "A",
          },
          {
            id: 2,
            title: "List item",
            subtitle: "Supporting line text lorem ipsum dolor...",
            avatar: "A",
          },
          {
            id: 3,
            title: "List item",
            subtitle: "Supporting line text lorem ipsum dolor...",
            avatar: "A",
          },
        ]}
      />

      <SearchViewModal
        variant="fullscreen"
        open={fullscreenSearchOpen}
        onClose={() => setFullscreenSearchOpen(false)}
        searchResults={[
          {
            id: 1,
            title: "List item",
            subtitle: "Supporting line text lorem ipsum dolor...",
            avatar: "A",
          },
          {
            id: 2,
            title: "List item",
            subtitle: "Supporting line text lorem ipsum dolor...",
            avatar: "A",
          },
          {
            id: 3,
            title: "List item",
            subtitle: "Supporting line text lorem ipsum dolor...",
            avatar: "A",
          },
        ]}
      />

      <SearchViewModal
        variant="compact"
        open={compactSearchOpen}
        onClose={() => setCompactSearchOpen(false)}
        searchResults={[
          {
            id: 1,
            title: "List item",
            avatar: "A",
          },
          {
            id: 2,
            title: "List item",
            avatar: "A",
          },
          {
            id: 3,
            title: "List item",
            avatar: "A",
          },
        ]}
      />
    </Container>
  );
};

export default ComponentsViewer;
